/**
 * AuthContext — single source of truth for the operator's session in
 * the admin UI.
 *
 * On mount, hits `GET /auth/me`. Three resolved states:
 *   - `status: 'loading'`     — initial fetch in flight
 *   - `status: 'authenticated'` — /auth/me returned 200 with user payload
 *   - `status: 'unauthenticated'` — /auth/me returned 401 (any other
 *     error is also treated as unauthenticated; surfacing an error
 *     screen for transient backend hiccups would just block login)
 *
 * `login(email, password)` POSTs `/auth/login` and on 200 sets the user
 * synchronously (no extra `/auth/me` roundtrip). The session cookie is
 * `HttpOnly`, so we can't read it from JS — we rely on the login
 * response body for the user payload.
 *
 * `logout()` POSTs `/auth/logout` (revokes the session row + clears
 * the cookie) and resets state. Network failure still resets local
 * state — the user wanted to log out, and the cookie either got cleared
 * or will expire on its own.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { api, ApiError } from './api';

export interface AuthUser {
  id: string;
  email: string;
  /** OCP-755 - super_admin | operator | triage. */
  role: 'super_admin' | 'operator' | 'triage';
  kind?: 'human' | 'bot';
  /** OCP-755 - a verified TOTP second factor. */
  mfa_enabled?: boolean;
  /** OCP-755 - a super_admin without a second factor: only /security works until it is set up. */
  mfa_enrollment_required?: boolean;
}

/** OCP-755 - the second login step: a current authenticator code or one backup code. */
export type LoginFactor = { totp_code: string } | { backup_code: string };

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  login: (email: string, password: string, factor?: LoginFactor) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-read /auth/me (after enrolling a second factor). */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    api<AuthUser>('/auth/me', { signal: ac.signal })
      .then((u) => {
        setUser(u);
        setStatus('authenticated');
      })
      .catch((err) => {
        if (ac.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setUser(null);
        setStatus('unauthenticated');
      });
    return () => ac.abort();
  }, []);

  const login = useCallback(async (email: string, password: string, factor?: LoginFactor) => {
    const u = await api<AuthUser>('/auth/login', {
      method: 'POST',
      body: { email, password, ...(factor ?? {}) },
    });
    setUser(u);
    setStatus('authenticated');
  }, []);

  const refresh = useCallback(async () => {
    try {
      setUser(await api<AuthUser>('/auth/me'));
      setStatus('authenticated');
    } catch {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // Best-effort. Local state still resets so the UI bounces to /login.
    }
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

export { ApiError };
